import { ToolDefinition } from "../types";

const encryptTextOnline: ToolDefinition = {
  slug: "encrypt-text-online",
  name: "Encrypt Text Online | AES-256-GCM and ChaCha20 Tool",
  title: "Encrypt Text Online",
  description:
    "Securely encrypt text online using AES-256-GCM and ChaCha20. Enter your plaintext and secret key to instantly generate base64 encrypted text for safe sharing.",
  longDescription:
    "Protect your private messages with our reversible text encryption tool. Choose between industry-standard AES-256-GCM or ChaCha20 algorithms, apply your secret key, and instantly generate a secure, copyable base64 encrypted payload.",
  toolType: {
    label: "Text Encryptor",
    icon: "Lock",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "AES-256-GCM Support",
    "ChaCha20 Support",
    "Base64 Output Generation",
  ],
  category: "Security Tools",
  categorySlug: "security-tools",
  href: "/tools/encrypt-text-online",
  api: "/api/v1/encrypt",
  keywords: [
    "snappy text encryption tool",
    "encrypt message",
    "aes cipher maker",
    "aes 256 gcm tool",
    "chacha20 cipher tool",
    "text encryptor",
    "string encryption app",
    "base64 cipher output",
    "reversible data protection",
    "secret key encryption",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Encrypt Text Online",
        description:
          "Follow these straightforward steps to encrypt your plaintext and generate a secure base64 cipher output.",
      },
      benefits: {
        title: "Why Use Our Text Encryption Tool",
        description:
          "Discover how our secure cipher text generator simplifies the process of protecting sensitive information.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Learn more about reversible text encryption, required secret keys, and how our supported algorithms work.",
      },
    },
    intro: {
      title: "Secure Text Encryption Made Simple",
      paragraphs: [
        "In an era where data privacy is paramount, having a reliable online encryption tool is essential for developers, security researchers, and privacy-conscious users. Our tool allows you to encrypt text online instantly using modern, authenticated cryptographic algorithms. Whether you need to secure a private note, test an application's backend payload, or securely mask a configuration string, this text encryptor provides a seamless workflow.",
        "By leveraging reversible text encryption protocols like AES-256-GCM and ChaCha20, you can lock your plaintext behind a custom secret key. The result is a robust base64 encrypted text payload that bundles both the unique nonce and the ciphertext. This ensures that your encrypted message is not only protected from unauthorized viewing but also mathematically verified against tampering when decrypted later.",
      ],
      note: "This tool strictly performs symmetric encryption. You must securely store the key you use, as it cannot be recovered if lost.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "Understanding Supported Algorithms",
        level: "h2",
        paragraphs: [
          "When you choose to encrypt a message online, selecting the right algorithm is crucial for both security and performance. This tool supports two of the most trusted authenticated encryption algorithms used in modern security protocols: AES-256-GCM and ChaCha20.",
          "AES-256-GCM (Advanced Encryption Standard with Galois/Counter Mode) is a widely adopted block cipher that provides both confidentiality and data origin authentication. It is the gold standard for secure data transmission and is heavily utilized in TLS certificates and secure messaging apps. Using our tool for AES 256 GCM encryption online ensures your data meets rigorous security standards.",
          "Alternatively, ChaCha20 is a highly efficient stream cipher. Often paired with Poly1305 for authentication, it is renowned for its exceptional speed and security, especially on devices lacking dedicated cryptographic hardware. Choosing ChaCha20 encryption online is perfect when you need a fast, secure, and modern alternative to standard AES.",
        ],
        note: "Both algorithms generate a base64 encoded string combining the required initialization vector (nonce) and the resulting ciphertext.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Critical Encryption Requirements",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Secret Key is Mandatory",
                icon: "Key",
                description:
                  "To encrypt text with a password or key, you must provide a strong secret key. The system requires this key to lock the data securely.",
                accent: "primary",
              },
              {
                label: "Key Management is Your Responsibility",
                icon: "ShieldAlert",
                description:
                  "We do not store, manage, or generate keys automatically. If you lose your key, the encrypted string cannot be reversed.",
                accent: "primary",
              },
              {
                label: "Base64 Output Format",
                icon: "Binary",
                description:
                  "The output is heavily structured. It returns base64 nonce ciphertext, making it easy to copy and transport without formatting issues.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Encryption vs. Hashing",
            description:
              "Understanding the core differences between encrypting a message and hashing it.",
            columns: [
              "Feature",
              "Symmetric Encryption (AES/ChaCha)",
              "Hashing (SHA-256)",
            ],
            rows: [
              {
                Feature: "Core Function",
                "Symmetric Encryption (AES/ChaCha)":
                  "Scrambles data to hide it securely",
                "Hashing (SHA-256)":
                  "Creates a unique, fixed-size digital footprint",
              },
              {
                Feature: "Reversibility",
                "Symmetric Encryption (AES/ChaCha)":
                  "Yes, if the correct secret key is provided",
                "Hashing (SHA-256)": "No, it is mathematically one-way",
              },
              {
                Feature: "Key Requirement",
                "Symmetric Encryption (AES/ChaCha)":
                  "Requires a secret key for both directions",
                "Hashing (SHA-256)": "No key required (for standard hashes)",
              },
              {
                Feature: "Output Length",
                "Symmetric Encryption (AES/ChaCha)":
                  "Variable, based on the input text length",
                "Hashing (SHA-256)": "Always a fixed length",
              },
            ],
            note: "This tool performs reversible encryption. If you need a one-way fingerprint, use a hashing tool instead.",
            emphasisColumns: ["Feature"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    callouts: [
      {
        title: "Important Limitations",
        layout: "list",
        iconColor: "primary",
        items: [
          {
            label: "No File Encryption",
            icon: "FileMinus",
            description:
              "This tool is designed specifically as a private text encryptor online. It does not accept or process file uploads.",
            accent: "primary",
          },
          {
            label: "No Decryption on this Route",
            icon: "Unlock",
            description:
              "This tool focuses solely on generating the cipher payload. To recover plaintext, you must use our dedicated decryption tool.",
            accent: "primary",
          },
        ],
      },
    ],
    referenceTables: [
      {
        title: "Output Structure Reference",
        description:
          "A breakdown of how the final base64 string is constructed by our backend.",
        columns: ["Component", "Description", "Size Example (Bytes)"],
        rows: [
          {
            Component: "Nonce (IV)",
            Description:
              "A unique, randomly generated number used once to ensure identical texts produce different cipher outputs.",
            "Size Example (Bytes)": "12 Bytes (Standard for GCM/Poly1305)",
          },
          {
            Component: "Ciphertext",
            Description:
              "The actual encrypted message data, obscured by the algorithm and key.",
            "Size Example (Bytes)": "Matches input length",
          },
          {
            Component: "Auth Tag",
            Description:
              "A cryptographic signature appended to ensure the ciphertext is not tampered with.",
            "Size Example (Bytes)": "16 Bytes",
          },
        ],
        note: "The entire package (Nonce + Ciphertext + Tag) is combined and encoded into Base64 for safe transport.",
        emphasisColumns: ["Component"],
        monospaceColumns: [],
      },
    ],
    faqs: [
      {
        question: "How do I encrypt text online?",
        answer:
          "To encrypt text online, simply paste your plaintext into the tool's input area, select your preferred secure algorithm (like AES-256-GCM or ChaCha20), and provide a secret key. Click the encrypt button, and the tool will instantly output a base64 encoded string containing your securely encrypted message. You can then copy the encrypted message for storage or transmission.",
      },
      {
        question: "What is AES-256-GCM encryption?",
        answer:
          "AES-256-GCM is an Advanced Encryption Standard operating in Galois/Counter Mode with a 256-bit key length. It is an authenticated encryption cipher, meaning it not only encrypts the data to keep it confidential but also generates an authentication tag to verify that the data has not been altered. It is highly secure and widely used in global security protocols.",
      },
      {
        question: "What is ChaCha20 used for?",
        answer:
          "ChaCha20 is a high-speed stream cipher developed as a modern alternative to traditional block ciphers like AES. It is widely used in mobile environments, TLS protocols, and VPNs because it offers exceptional security and performance without relying on dedicated hardware acceleration. Using it to encrypt a message online guarantees rapid, secure processing.",
      },
      {
        question: "Why is a key required for encryption?",
        answer:
          "Symmetric reversible text encryption relies entirely on a shared secret. The key acts as the mathematical 'lock' that scrambles the plaintext into an unreadable format. Without a key, the algorithm has no unique parameters to secure the data. Anyone who possesses the correct key can reverse the process, which is why keeping your key private is critical.",
      },
      {
        question: "Can encrypted text be decrypted later?",
        answer:
          "Yes. The encryption methods used here (AES and ChaCha20) are fully reversible. As long as you keep the exact base64 encrypted text output and remember the exact secret key you used during the encryption process, you can easily reverse the payload back into plaintext using a compatible decryption tool.",
      },
      {
        question: "What does nonce plus ciphertext mean?",
        answer:
          "A nonce is a 'number used once'—a randomly generated value that initiates the encryption process. By attaching the nonce to the front of the ciphertext (the scrambled data) before encoding it into base64, we ensure that the decryption tool has the exact parameters needed to unlock the data later. The nonce itself is not a secret, but it must be unique for every encryption.",
      },
      {
        question: "Is encryption the same as hashing?",
        answer:
          "No. Encryption is a two-way (reversible) process designed to hide data safely so it can be unlocked later with a specific key. Hashing, such as SHA-256, is a one-way mathematical function that converts data into a fixed-length string to verify its integrity. Hashes cannot be decrypted back into their original text.",
      },
    ],
    benefits: [
      {
        icon: "ShieldCheck",
        title: "Robust Algorithmic Security",
        description:
          "Utilizes trusted, authenticated ciphers like AES-256-GCM and ChaCha20 to ensure maximum protection for your data.",
      },
      {
        icon: "Copy",
        title: "Easy to Copy Base64 Output",
        description:
          "Automatically bundles your ciphertext and nonce into a single, clean base64 string that is safe to copy and share across any platform.",
      },
      {
        icon: "Zap",
        title: "Fast and Responsive",
        description:
          "Instantly generates your secure cipher text without complicated setup, making message encryption highly efficient.",
      },
      {
        icon: "Key",
        title: "Custom Key Control",
        description:
          "You remain in complete control of your security by providing your own secret key to lock and secure your text.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Paste Plaintext",
        description:
          "Enter or paste the private text message or string you wish to securely encrypt into the input field.",
      },
      {
        number: "02",
        title: "Select Cipher Algorithm",
        description:
          "Choose between the highly secure AES-256-GCM or the rapid ChaCha20 encryption algorithms from the dropdown menu.",
      },
      {
        number: "03",
        title: "Enter Secret Key",
        description:
          "Provide a strong, unique secret key. The system strictly requires this key to perform reversible encryption.",
      },
      {
        number: "04",
        title: "Encrypt and Copy",
        description:
          "Click to generate the secure payload. The tool will output a base64 encoded string containing the nonce and ciphertext for you to copy.",
      },
    ],
    useCases: [
      {
        label: "Securing Private Notes",
        icon: "FileText",
        description:
          "Safely encrypt notes with a secret key before saving them to cloud environments or sending them to a trusted contact.",
        accent: "primary",
      },
      {
        label: "API Payload Testing",
        icon: "Terminal",
        description:
          "Developers can manually generate secure base64 AES-GCM or ChaCha20 payloads to test backend decryption workflows.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Software Developers",
        icon: "Code",
        description:
          "Ideal for engineers who need to quickly generate and verify encrypted strings while building secure applications.",
        accent: "accent",
      },
      {
        label: "Privacy Advocates",
        icon: "EyeOff",
        description:
          "Useful for individuals seeking an online text encryption base64 output tool to quickly scramble sensitive communications.",
        accent: "accent",
      },
    ],
  },
};

export default encryptTextOnline;
export { encryptTextOnline };
